// Updated Statistics.js component with unique CSS classes
import React, { useState, useEffect } from 'react';
import api from './api';

const Statistics = () => {
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'dateSent', direction: 'desc' });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${process.env.REACT_APP_API_URL}/get-statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedStatistics = React.useMemo(() => {
    let sortableStats = [...statistics];
    if (sortConfig.key) {
      sortableStats.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle date sorting
        if (sortConfig.key === 'dateSent') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        // Handle numeric sorting
        if (typeof aValue === 'string' && !isNaN(aValue)) {
          aValue = parseFloat(aValue);
        }
        if (typeof bValue === 'string' && !isNaN(bValue)) {
          bValue = parseFloat(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableStats;
  }, [statistics, sortConfig]);

  const filteredStatistics = sortedStatistics.filter(stat =>
    stat.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('nl-NL');
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const getSortClass = (columnName) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === 'asc' ? 'stats-sorted-asc' : 'stats-sorted-desc';
    }
    return '';
  };

  const showUnopenedEmails = (emails) => {
    if (emails.length === 0) {
      alert('Alle ontvangers hebben deze e-mail geopend!');
      return;
    }
    
    const emailList = emails.join('\n');
    alert(`E-mails die nog niet zijn geopend (${emails.length}):\n\n${emailList}`);
  };

  if (loading) {
    return (
      <div className="statistics-modal-container">
        <h2>Email Statistics</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="statistics-modal-container">
      <input
        type="text"
        placeholder="Search by subject..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="statistics-search-input"
      />

      <div className="statistics-table-wrapper">
        <table className="statistics-data-table">
          <thead>
            <tr>
              <th 
                onClick={() => handleSort('subject')}
                className={getSortClass('subject')}
              >
                Subject{getSortIcon('subject')}
              </th>
              <th 
                onClick={() => handleSort('dateSent')}
                className={getSortClass('dateSent')}
              >
                Date Sent{getSortIcon('dateSent')}
              </th>
              <th 
                onClick={() => handleSort('sent')}
                className={getSortClass('sent')}
              >
                Sent{getSortIcon('sent')}
              </th>
              <th 
                onClick={() => handleSort('uniqueOpens')}
                className={getSortClass('uniqueOpens')}
              >
                Unique Opens{getSortIcon('uniqueOpens')}
              </th>
              <th 
                onClick={() => handleSort('totalOpens')}
                className={getSortClass('totalOpens')}
              >
                Total Opens{getSortIcon('totalOpens')}
              </th>
              <th 
                onClick={() => handleSort('uniqueClicks')}
                className={getSortClass('uniqueClicks')}
              >
                Unique Clicks{getSortIcon('uniqueClicks')}
              </th>
              <th 
                onClick={() => handleSort('percentUniqueOpens')}
                className={getSortClass('percentUniqueOpens')}
              >
                % Unique Opens{getSortIcon('percentUniqueOpens')}
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredStatistics.map((stat, index) => (
              <tr key={index}>
                <td data-label="Onderwerp">{stat.subject}</td>
                <td data-label="Datum">{formatDate(stat.dateSent)}</td>
                <td data-label="Verzonden">{stat.sent}</td>
                <td data-label="Geopend">{stat.uniqueOpens}</td>
                <td data-label="Totaal Opens">{stat.totalOpens}</td>
                <td data-label="Geklikt">{stat.uniqueClicks}</td>
                <td data-label="% Geopend">{stat.percentUniqueOpens}%</td>
                <td data-label="Actie">
                  {stat.unopenedEmails && stat.unopenedEmails.length > 0 ? (
                    <button 
                      className="statistics-unopened-button"
                      onClick={() => showUnopenedEmails(stat.unopenedEmails)}
                    >
                      Niet Geopend ({stat.unopenedEmails.length})
                    </button>
                  ) : (
                    <span className="statistics-success-message">
                      Allemaal Geopend ✓
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredStatistics.length === 0 && (
        <p className="statistics-no-results">
          {searchTerm ? 'No statistics found matching your search.' : 'No email statistics available.'}
        </p>
      )}
    </div>
  );
};

export default Statistics;